import React, {
  lazy, memo, Suspense, useCallback, useEffect, useMemo, useState,
} from 'react'

import {
  Badge,
  Icon,
  Button,
  ChevronDown,
  MoreFilters as MoreFiltersIcon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import AdvancedFiltersContent from 'Pods/SearchPage/Shared/AdvancedFiltersContent/AdvancedFiltersContent'
import DatePublishedContent from 'Pods/SearchPage/Shared/DatePublishedContent/DatePublishedContent'
import DesignerContent from 'Pods/SearchPage/Shared/DesignerContent/DesignerContent'
import LicenseContent from 'Pods/SearchPage/Shared/LicenseContent/LicenseContent'
import QualityContent from 'Pods/SearchPage/Shared/QualityContent/QualityContent'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { clearDatePublished } from 'Pods/SearchPage/Store/actions/datePublished'
import { clearDesigners } from 'Pods/SearchPage/Store/actions/designers'
import { addFileType, removeFileType, clearFileTypes } from 'Pods/SearchPage/Store/actions/fileTypes'
import { clearLicenses } from 'Pods/SearchPage/Store/actions/licenses'
import { clearMoreFilters } from 'Pods/SearchPage/Store/actions/moreFilters'
import { clearPolygon } from 'Pods/SearchPage/Store/actions/polygon'
import { clearPricing } from 'Pods/SearchPage/Store/actions/pricing'
import { clearQualityContent } from 'Pods/SearchPage/Store/actions/qualityContent'
import { clearSimulationReady } from 'Pods/SearchPage/Store/actions/simulationReady'
import { clearSize } from 'Pods/SearchPage/Store/actions/size'
import { clearStyles } from 'Pods/SearchPage/Store/actions/styles'
import { clearTags } from 'Pods/SearchPage/Store/actions/tags'
import { clearThreeDOnly } from 'Pods/SearchPage/Store/actions/threeDOnly'
import { clearThreeDprintOnly } from 'Pods/SearchPage/Store/actions/threeDPrintOnly'
import { DATE_PUBLISHED, DEFAULT_STATE, LICENSE_TYPE, QUALITY_LEVEL } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import { getFiltersCount, getSecondaryFiltersCount } from 'Pods/SearchPage/Store/selectors'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

// Keeps the drawer (and its CollapsibleSection subtree) out of the initial search pack. Safe to
// suspend: it renders only after the compact-breakpoint trigger is clicked, so this boundary is
// never reached during the page's SSR pass. Mirrors the CustomRange split in DatePublishedContent.
const MoreFiltersDrawer = lazy(() => import(
  /* webpackChunkName: "search-more-filters-drawer" */ 'Pods/LazyLoaded/MoreFiltersDrawer/MoreFiltersDrawer'
))

const prefixIcon = <Icon notSpaced svgComponent={MoreFiltersIcon} color="dark" size="small" />

const trailIcon = (
  <Icon
    notSpaced
    svgComponent={ChevronDown}
    color="darker"
    className="more-filters__trigger-chevron"
  />
)

// Below md the bar collapses, so the filters open in a full-screen modal behind a "Filters" button.
const COMPACT_BREAKPOINTS = ['xs', 'sm']
const XL_SLOT_BREAKPOINTS = ['xl', 'xxl']
const DEFAULT_LICENSES = [LICENSE_TYPE.ALL]
const DEFAULT_QUALITY = [QUALITY_LEVEL.ANY]

function MoreFilters() {
  const translate = useTranslate()

  const advancedFilters = useSearchPageStore(state => state.advancedFilters)
  const personalFilter = useSearchPageStore(state => state.personalFilter)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  const fileTypes = useSearchPageStore(state => state.fileTypes ?? EMPTY_ARRAY)
  const onSale = useSearchPageStore(state => state.onSale)
  const withSubscription = useSearchPageStore(state => state.withSubscription)
  const priceRanges = useSearchPageStore(state => state.priceRanges ?? EMPTY_ARRAY)
  const minPrice = useSearchPageStore(state => state.minPrice ?? '')
  const maxPrice = useSearchPageStore(state => state.maxPrice ?? '')
  const licenses = useSearchPageStore(state => state.licenses ?? DEFAULT_LICENSES)
  const aiUsageAllowed = useSearchPageStore(state => state.aiUsageAllowed ?? false)
  const datePublished = useSearchPageStore(state => state.datePublished ?? DATE_PUBLISHED.ALL_TIME)
  const quality = useSearchPageStore(state => state.quality ?? DEFAULT_QUALITY)
  const contentSource = useSearchPageStore(state => state.contentSource ?? DEFAULT_STATE.contentSource)
  const designers = useSearchPageStore(state => state.designers ?? EMPTY_ARRAY)
  const designerScopes = useSearchPageStore(state => state.designerScopes ?? EMPTY_ARRAY)
  const hasSecondaryFilters = useSearchPageStore(state => getSecondaryFiltersCount(state) > 0)
  // Below 480 the Clear button is hidden, so the compact trigger carries the count as a badge.
  const activeFiltersCount = useSearchPageStore(state => getFiltersCount(state))
  const breakpoint = useBreakpoint()
  // Gate the popover/modal split until mounted so the first client render matches SSR
  // (useBreakpoint is undefined on the server), avoiding a hydration mismatch.
  const [hasMounted, setHasMounted] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const isCompact = hasMounted && COMPACT_BREAKPOINTS.includes(breakpoint)
  // Quality/License live in main-filters__slot--lg (1280+); at md (960-1279) their own
  // dropdowns are hidden, so they overflow into this popover to stay reachable. Price has its own
  // dropdown from md (Figma 415:23304).
  const showLgSlotOverflow = hasMounted && breakpoint === 'md'
  // Date Published lives in main-filters__slot--xl (1600+); below it (down to md) its own dropdown
  // is hidden, so it overflows into this popover. Compact uses the drawer.
  const showXlSlotOverflow = hasMounted && !isCompact && !XL_SLOT_BREAKPOINTS.includes(breakpoint)
  // Figma 2805:153402 keeps Designer inside More filters at every desktop width.
  const showDesignerSection = hasMounted && !isCompact

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  useEffect(() => {
    if (!isCompact && isDrawerOpen) {
      setIsDrawerOpen(false)
    }
  }, [isCompact, isDrawerOpen])

  // Filters owned by the More Filters popover itself; drives the desktop trigger and its Reset.
  const hasOwnFilters = useMemo(() => {
    return (
      advancedFilters.length > 0 ||
      personalFilter != null ||
      purchaseFilter != null
    )
  }, [advancedFilters, personalFilter, purchaseFilter])

  // On compact breakpoints the trigger opens the All Filters modal, which also hosts
  // File Format, Price and Date Published — those count toward the trigger's active state there only.
  const hasModalFilters = useMemo(() => {
    return (
      hasOwnFilters ||
      fileTypes.length > 0 ||
      onSale ||
      withSubscription ||
      priceRanges.length > 0 ||
      minPrice !== '' ||
      maxPrice !== '' ||
      licenses[0] !== LICENSE_TYPE.ALL ||
      aiUsageAllowed ||
      quality[0] !== QUALITY_LEVEL.ANY ||
      contentSource !== DEFAULT_STATE.contentSource ||
      datePublished !== DATE_PUBLISHED.ALL_TIME ||
      designers.length > 0 ||
      designerScopes.length > 0 ||
      hasSecondaryFilters
    )
  }, [
      hasOwnFilters,
      fileTypes,
      onSale,
      withSubscription,
      priceRanges,
      minPrice,
      maxPrice,
      licenses,
      aiUsageAllowed,
      quality,
      contentSource,
      datePublished,
      designers,
      designerScopes,
      hasSecondaryFilters,
    ])

  // Filters from the lg-slot dropdowns hidden at md; they count toward the desktop
  // trigger's active state only while they overflow into this popover.
  const hasOverflowFilters = useMemo(() => {
    return (
      quality[0] !== QUALITY_LEVEL.ANY ||
      contentSource !== DEFAULT_STATE.contentSource ||
      licenses[0] !== LICENSE_TYPE.ALL ||
      aiUsageAllowed
    )
  }, [
      quality,
      contentSource,
      licenses,
      aiUsageAllowed,
    ])

  const hasDesignerFilters = designers.length > 0 || designerScopes.length > 0

  const hasPopoverFilters = hasOwnFilters
    || (showLgSlotOverflow && hasOverflowFilters)
    || (showXlSlotOverflow && datePublished !== DATE_PUBLISHED.ALL_TIME)
    || (showDesignerSection && hasDesignerFilters)

  const handleReset = useCallback(() => {
    clearMoreFilters()
    if (showLgSlotOverflow) {
      clearQualityContent()
      clearLicenses()
    }
    if (showXlSlotOverflow) {
      clearDatePublished()
    }
    if (showDesignerSection) {
      clearDesigners()
    }
  }, [showLgSlotOverflow, showXlSlotOverflow, showDesignerSection])

  const handleFileTypeChange = useCallback((_, value, isOn) => {
    if (isOn) {
      addFileType(value)
    } else {
      removeFileType(value)
    }
  }, [])

  const handleModalReset = useCallback(() => {
    clearFileTypes()
    clearPricing()
    clearLicenses()
    clearQualityContent()
    clearDatePublished()
    clearMoreFilters()
    clearDesigners()
    // Features section (3D-only): clears the technical attributes + the low-poly polygon range.
    clearThreeDOnly()
    clearPolygon()
    // The drawer's Features section swaps in PRINT_FEATURES for a 3D-print search and hides
    // their section-level reset, so this footer Reset is their only clear path.
    clearThreeDprintOnly()
    clearStyles()
    clearSize()
    clearTags()
    clearSimulationReady()
  }, [])

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpen(true)
  }, [])

  const content = useMemo(() => (
    <>
      {showLgSlotOverflow && (
        <>
          <div className="more-filters__overflow-title cgt-overline-md">
            {translate('search_page.filters.quality.title')}
          </div>
          <QualityContent hideQualityLabel />
          <div className="cgt-divider" aria-hidden="true" />
          <div className="more-filters__overflow-title cgt-overline-md">
            {translate('search_page.filters.license.title')}
          </div>
          <LicenseContent hideGroupLabels />
          <div className="cgt-divider" aria-hidden="true" />
        </>
      )}
      {showXlSlotOverflow && (
        <>
          <div className="more-filters__overflow-title cgt-overline-md">
            {translate('search_page.filters.date_published.title')}
          </div>
          <DatePublishedContent />
          <div className="cgt-divider" aria-hidden="true" />
        </>
      )}
      {showDesignerSection && (
        <>
          <div className="more-filters__overflow-title cgt-overline-md">
            {translate('search_page.filters.designers.title')}
          </div>
          {/* The popover's footer Reset clears designers, so the section hides its own. */}
          <DesignerContent hideReset />
          <div className="cgt-divider" aria-hidden="true" />
        </>
      )}
      <AdvancedFiltersContent />
      <Button
        type="ghost"
        size="tiny"
        customClass="filter-wrapper__reset-button"
        onClick={handleReset}
        disabled={!hasPopoverFilters}
      >
        {translate('search_page.filters.more_filters.reset')}
      </Button>
    </>
  ), [
    handleReset,
    hasPopoverFilters,
    showLgSlotOverflow,
    showXlSlotOverflow,
    showDesignerSection,
  ])

  if (isCompact) {
    const hasActiveFilters = activeFiltersCount > 0
    // Figma 415:23612: from 640 the row has room for the full "More filters" label; phones keep "Filters".
    const isFullLabel = breakpoint === 'sm'
    const triggerLabel = isFullLabel
      ? translate('search_page.filters.more_filters.desktop_title')
      : translate('search_page.filters.more_filters.trigger_label')
    const triggerLabelWithCount = isFullLabel
      ? `${triggerLabel} (${activeFiltersCount})`
      : translate('search_page.filters.more_filters.trigger_label_with_count', { count: activeFiltersCount })

    return (
      <>
        <Button
          type="secondary"
          variant="with-icon"
          icon={prefixIcon}
          isActive={hasModalFilters}
          onClick={handleOpenDrawer}
          customClass="filter-wrapper__button more-filters__trigger"
          aria-label={hasActiveFilters ? triggerLabelWithCount : triggerLabel}
          aria-expanded={isDrawerOpen}
          trailIcon={trailIcon}
        >
          <span className="more-filters__trigger-label">
            {triggerLabel}
          </span>
          {hasActiveFilters && (
            <Badge type="primary" className="more-filters__trigger-badge">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
        {isDrawerOpen && (
          <Suspense fallback={null}>
            <MoreFiltersDrawer
              onClose={handleCloseDrawer}
              onReset={handleModalReset}
              fileTypes={fileTypes}
              onFileTypeChange={handleFileTypeChange}
            />
          </Suspense>
        )}
      </>
    )
  }

  return (
    <FiltersWrapper
      // Figma 415:23304 shortens the trigger to "Filters" at md so the row fits next to Price.
      placeholder={showLgSlotOverflow
        ? translate('search_page.filters.more_filters.trigger_label')
        : translate('search_page.filters.more_filters.desktop_title')}
      prefixIcon={prefixIcon}
      content={content}
      hasSelectedAnyFilters={hasPopoverFilters}
      closingDropdownClassName="filter-wrapper"
      contentClassName="more-filters__popover-content"
    />
  )
}

export default memo(MoreFilters)
